import { useCallback } from "react";
import { Collapse, Typography } from "@mui/material";
import DoctorCard from "../../../components/dashboard/doctorCard/DoctorCard";
import InsuranceCard from "../../../components/dashboard/InsuranceCard/InsuranceCard";
import { getPercentageByTotal } from "../../../utilis/common";
import { DECEASED_CARDS_LABELS, DECEASED_CARDS_TYPE } from "../../../types/deceased.type";
import { PAGE_TYPE } from "../../../types/pages.type";
import DayCardList from "../../hospital/components/dayCardList/DayCardList";
import FloorCardList from "../../hospital/components/floorCardList/FloorCardList";
import NinetyDaysAnalysis from "../../hospital/components/ninetyDaysAnalysis/NinetyDaysAnalysis";
import DynamicDataCardList from "../../hospital/components/DynamicDataCardList/DynamicDataCardList";
import { findHospitalDynamicCardByKey, HeaderFilterCardShell, PanelWrap } from "../../hospital/components/HospitalHeaderCardFilterPanel";

function DeceasedHeaderDynamicFilterBody({
	ele,
	accessor,
	cardFilter,
	filterListData,
	filterListDataComparison,
	handleToggle,
	handleToggleAll,
	filter,
	filterComparison,
	loadingBoth,
	dbData,
	dbDataComparison,
	percentageAgainst,
	averageCensusComparison,
	projectionDays,
	getPriorityNumber,
	transferType,
	lockedTotalBy,
	filterTotal,
}) {
	const cardSelectedItems = cardFilter?.[accessor] ?? [];
	const cardID = filterListData?.[accessor]?.[0]?.id;
	const isSelectedTotalCard = cardSelectedItems.indexOf(cardID) !== -1;
	const isCustomTab = Boolean(ele?.isCustomTab);
	const question = ele?.question;

	const handleCardClick = useCallback(
		async (_titleParam = null, uniqueKey = null) => {
			const compositeKey = uniqueKey && accessor ? `${accessor}__${uniqueKey}` : null;
			const comparisonItem = filterListDataComparison?.[accessor]?.[0];
			await handleToggle({
				item: filterListData?.[accessor]?.[0],
				itemComparison: comparisonItem,
				type: accessor,
				isChecked: isSelectedTotalCard,
				cardTitle: ele.title || ele.label || ele.tableLabel,
				question: {
					isCustom: true,
					customQuestionInputType: question?.customQuestionInputType,
					isCustomTab: false,
					cardTitle: ele.title || ele.label || ele.tableLabel,
					customTab: ele.type,
					accessor,
					compositeKey,
					uniqueKey,
				},
				isCustomTab: false,
			});
		},
		[accessor, ele, filterListData, filterListDataComparison, handleToggle, isSelectedTotalCard, question]
	);

	return (
		<DynamicDataCardList
			compactForHeaderPopover
			disableInteractions={false}
			totalTransfers={dbData.total}
			totalTransfersComparison={dbDataComparison?.total}
			filterListData={filterListData}
			filterListDataComparison={filterListDataComparison}
			question={ele}
			cardTitle={ele.title || ele.label || ele.tableLabel}
			data={filterListData?.[accessor] ?? []}
			dataComparison={filterListDataComparison?.[accessor] ?? []}
			handleToggle={handleToggle}
			handleToggleAll={handleToggleAll}
			selectedItem={cardFilter?.[accessor]}
			spacialSelectedItem={cardFilter?.[`${accessor}_spacial`]}
			type={accessor}
			filter={filter}
			loading={loadingBoth}
			filterComparison={filterComparison}
			page={PAGE_TYPE.DECEASED}
			averageCensus={percentageAgainst}
			projectionDays={projectionDays}
			isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
			priorityNumber={getPriorityNumber(accessor)}
			transferType={transferType}
			lockedTotalBy={lockedTotalBy}
			isCustomTab={isCustomTab}
			filterTotal={filterTotal}
			handleCardClick={handleCardClick}
			isSelectedTotalCard={isSelectedTotalCard}
			cardFilter={cardFilter}
			isDisabled={false}
			admissionCompareAgainst={null}
			admissionCompareAgainstComparison={null}
			reverseColorsAdmissionPage={false}
			averageCensusComparison={averageCensusComparison}
		/>
	);
}

/**
 * Interactive filter popover bodies for Deceased dashboard header chips (same handlers/data as main cards).
 */
export default function DeceasedHeaderCardFilterPanel({
	dataKey,
	handleToggle,
	handleToggleAll,
	filterListData,
	filterListDataComparison,
	cardFilter,
	filter,
	filterComparison,
	loading,
	loadingComparison,
	projectionDays,
	filterTotal,
	filterTotalComparison,
	dbData,
	dbDataComparison,
	specialComparisonSet = false,
	transferType,
	lockedTotalBy,
	getPriorityNumber,
	dynamicCards = [],
	deceasedDynamicTabDataByAccessor = {},
	percentageAgainst,
	headerFilterPopoverOpen,
}) {
	const loadingBoth = loading || loadingComparison;
	const averageCensus = specialComparisonSet ? filterTotal : getPercentageByTotal(dbData);
	const averageCensusComparison = specialComparisonSet ? filterTotalComparison : getPercentageByTotal(dbDataComparison);

	const panelContent = (() => {
		switch (dataKey) {
		case DECEASED_CARDS_TYPE.INSURANCE_DATA:
			return (
				<HeaderFilterCardShell
					title={DECEASED_CARDS_LABELS.insuranceData}
					bodySx={{ p: 0, pt: 0.5, overflow: "visible" }}
				>
					<InsuranceCard
						cardTitle={DECEASED_CARDS_LABELS.insuranceData}
						data={filterListData.insuranceData}
						dataComparison={filterListDataComparison?.insuranceData}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						selectedItem={cardFilter.insuranceData}
						type={DECEASED_CARDS_TYPE.INSURANCE_DATA}
						page={PAGE_TYPE.DECEASED}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						headerFilterPopover
						hideProgressBar
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(DECEASED_CARDS_TYPE.INSURANCE_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						isMobile={false}
					/>
				</HeaderFilterCardShell>
			);

		case DECEASED_CARDS_TYPE.NINETY_DAYS_DATA:
			return (
				<PanelWrap title={DECEASED_CARDS_LABELS.ninetyDaysData}>
					<NinetyDaysAnalysis
						cardTitle={DECEASED_CARDS_LABELS.ninetyDaysData}
						handleToggle={handleToggle}
						loading={loadingBoth}
						selectedItem={cardFilter.ninetyDaysData}
						type={DECEASED_CARDS_TYPE.NINETY_DAYS_DATA}
						data={filterListData.ninetyDaysData}
						comparisonData={filterListDataComparison?.ninetyDaysData}
						page={PAGE_TYPE.DECEASED}
						filterListData={filterListData}
						filterListDataComparison={filterListDataComparison}
						filter={filter}
						filterComparison={filterComparison}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						headerFilterPopover
						hideRadialSection
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(DECEASED_CARDS_TYPE.NINETY_DAYS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case DECEASED_CARDS_TYPE.FLOORS_DATA:
			return (
				<PanelWrap title={DECEASED_CARDS_LABELS.floorsData}>
					<FloorCardList
						cardTitle={DECEASED_CARDS_LABELS.floorsData}
						data={filterListData.floorsData}
						dataComparison={filterListDataComparison?.floorsData}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						loading={loadingBoth}
						selectedItem={cardFilter.floorsData}
						type={DECEASED_CARDS_TYPE.FLOORS_DATA}
						page={PAGE_TYPE.DECEASED}
						filter={filter}
						filterComparison={filterComparison}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(DECEASED_CARDS_TYPE.FLOORS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		case DECEASED_CARDS_TYPE.DOCTOR_DATA:
			return (
				<PanelWrap title={DECEASED_CARDS_LABELS.doctorData}>
					<DoctorCard
						cardTitle={DECEASED_CARDS_LABELS.doctorData}
						data={filterListData?.doctorData ?? []}
						comparisonData={filterListDataComparison?.doctorData}
						minimizable={true}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						selectedItem={cardFilter?.doctorData ?? []}
						type={DECEASED_CARDS_TYPE.DOCTOR_DATA}
						page={PAGE_TYPE.DECEASED}
						loading={loadingBoth}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						filter={filter}
						filterComparison={filterComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
						priorityNumber={getPriorityNumber(DECEASED_CARDS_TYPE.DOCTOR_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
						isMobile={false}
						forHeaderPopover
					/>
				</PanelWrap>
			);

		case DECEASED_CARDS_TYPE.DAYS_DATA:
			return (
				<PanelWrap title={DECEASED_CARDS_LABELS.daysData}>
					<DayCardList
						cardTitle={DECEASED_CARDS_LABELS.daysData}
						data={filterListData.daysData}
						dataComparison={filterListDataComparison?.daysData}
						handleToggle={handleToggle}
						selectedItem={cardFilter.daysData}
						filter={filter}
						loading={loadingBoth}
						filterComparison={filterComparison}
						type={DECEASED_CARDS_TYPE.DAYS_DATA}
						page={PAGE_TYPE.DECEASED}
						averageCensus={averageCensus}
						averageCensusComparison={averageCensusComparison}
						projectionDays={projectionDays}
						filterTotal={filterTotal}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(DECEASED_CARDS_TYPE.DAYS_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						disableInteractions={false}
					/>
				</PanelWrap>
			);

		default: {
			const dynamicEle =
				deceasedDynamicTabDataByAccessor[dataKey] ||
				findHospitalDynamicCardByKey(dataKey, dynamicCards);
			if (dynamicEle) {
				const accessor = dynamicEle.accessor || dataKey;
				const dynamicTitle =
					dynamicEle.title || dynamicEle.label || dynamicEle.tableLabel || String(dataKey);
				return (
					<PanelWrap title={dynamicTitle}>
						<DeceasedHeaderDynamicFilterBody
							ele={dynamicEle}
							accessor={accessor}
							cardFilter={cardFilter}
							filterListData={filterListData}
							filterListDataComparison={filterListDataComparison}
							handleToggle={handleToggle}
							handleToggleAll={handleToggleAll}
							filter={filter}
							filterComparison={filterComparison}
							loadingBoth={loadingBoth}
							dbData={dbData}
							dbDataComparison={dbDataComparison}
							percentageAgainst={percentageAgainst ?? averageCensus}
							averageCensusComparison={averageCensusComparison}
							projectionDays={projectionDays}
							getPriorityNumber={getPriorityNumber}
							transferType={transferType}
							lockedTotalBy={lockedTotalBy}
							filterTotal={filterTotal}
						/>
					</PanelWrap>
				);
			}
			return (
				<PanelWrap title="Filter">
					<Typography variant="body2" sx={{ p: 1, color: "text.secondary", fontSize: 12 }}>
						This filter is not available in the header preview.
					</Typography>
				</PanelWrap>
			);
		}
	}
	})();

	if (headerFilterPopoverOpen === undefined) {
		return panelContent;
	}

	return (
		<Collapse orientation="vertical" in={headerFilterPopoverOpen} timeout={1000} unmountOnExit={false}>
			{panelContent}
		</Collapse>
	);
}
